import {
  CostServiceState,
  CreateCostService,
  UpdateCostService,
} from "../../types/costService";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiResource } from "../../services/api";

const costServicesApi = apiResource("cost-services");

const initialState: CostServiceState = {
  costServices: [],
  currentCostService: null,
  loading: false,
  isLoaded: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get all cost services
export const getCostServices = createAsyncThunk(
  "costServices/getCostServices",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await costServicesApi.getAll(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cost services",
      );
    }
  },
);

// Get single cost service
export const getCostService = createAsyncThunk(
  "costServices/getCostService",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await costServicesApi.getOne(id);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cost service",
      );
    }
  },
);

// Create cost service
export const createCostService = createAsyncThunk(
  "costServices/createCostService",
  async (serviceData: CreateCostService, { rejectWithValue }) => {
    try {
      const response = await costServicesApi.create(serviceData);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create cost service",
      );
    }
  },
);

// Update cost service
export const updateCostService = createAsyncThunk(
  "costServices/updateCostService",
  async (
    { id, serviceData }: { id: string; serviceData: UpdateCostService },
    { rejectWithValue },
  ) => {
    try {
      const response = await costServicesApi.update(id, serviceData);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update cost service",
      );
    }
  },
);

// Delete cost service
export const deleteCostService = createAsyncThunk(
  "costServices/deleteCostService",
  async (id: string, { rejectWithValue }) => {
    try {
      await costServicesApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete cost service",
      );
    }
  },
);

const costServicesSlice = createSlice({
  name: "costServices",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCostService: (state) => {
      state.currentCostService = null;
    },
    resetCostServiceState: () => {
      return initialState;
    },
    setIsLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All
      .addCase(getCostServices.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isLoaded = false;
      })
      .addCase(getCostServices.fulfilled, (state, action) => {
        state.loading = false;
        state.costServices = action.payload.services;
        state.pagination = action.payload.pagination;
        state.error = null;
        state.isLoaded = true;
      })
      .addCase(getCostServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isLoaded = false;
      })

      // Get One
      .addCase(getCostService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCostService.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCostService = action.payload;
        state.error = null;
      })
      .addCase(getCostService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createCostService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCostService.fulfilled, (state, action) => {
        state.loading = false;
        state.costServices.unshift(action.payload);
        state.error = null;
      })
      .addCase(createCostService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update
      .addCase(updateCostService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCostService.fulfilled, (state, action) => {
        const index = state.costServices.findIndex(
          (service) => service._id === action.payload._id,
        );
        if (index !== -1) {
          state.costServices[index] = action.payload;
        }
        if (state.currentCostService?._id === action.payload._id) {
          state.currentCostService = action.payload;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCostService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete
      .addCase(deleteCostService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostService.fulfilled, (state, action) => {
        state.costServices = state.costServices.filter(
          (service) => service._id !== action.payload,
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteCostService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentCostService,
  resetCostServiceState,
  setIsLoaded,
} = costServicesSlice.actions;

export default costServicesSlice.reducer;
